import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { getRfiByType } from 'api/kyc';
import * as ROUTES from 'constants/routes';
import PartnerInitiatedKycs from 'modules/kyc/components/PartnerInitiatedKycs';
import RFiList from 'modules/kyc/components/RfiList';
import { DEFAULT_PAGE_SIZE, KYC_TAB_TITLES, RFI_STATUS, TAB_TYPE_MAP } from 'modules/kyc/constants/constants';
import { formatSummaryData } from 'modules/kyc/utils/utils';
import CreateKybLinkModal from 'modules/merchantinfo/components/CreateKybLinkModal';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

const RfiSummary = ({ summaryData, isLoading, error }) => {
  const [entityId, setEntityId] = useState('');
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});
  const [currentPages, setCurrentPages] = useState({ 1: 1, 2: 1, 3: 1, 4: 1 });
  const [totalCounts, setTotalCounts] = useState({ 1: 0, 2: 0, 3: 0, 4: 0 });
  const [isLastPage, setIsLastPage] = useState({ 1: true, 2: true, 3: true, 4: true });

  const globalLinkModal = useDisclosure();

  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const activeTab = Number(queryParams.get('tab')) || 0;
  const currentPage = activeTab > 0 ? currentPages[activeTab] : null;

  const fetchTabData = useCallback(async (tabIndex, page) => {
    const type = TAB_TYPE_MAP[tabIndex];

    if (!type) return;

    setTabLoading((prev) => ({ ...prev, [tabIndex]: true }));
    setTabError((prev) => ({ ...prev, [tabIndex]: null }));

    try {
      const response = await getRfiByType({ type, page, pageSize: DEFAULT_PAGE_SIZE });
      const data = response?.data?.data;

      setTabData((prev) => ({
        ...prev,
        [tabIndex]: data?.rfi_summary_details || [],
      }));
      setTotalCounts((prev) => ({
        ...prev,
        [tabIndex]: data?.total_count || 0,
      }));
      setIsLastPage((prev) => ({
        ...prev,
        [tabIndex]: data?.is_last_page ?? true,
      }));
    } catch (err) {
      setTabError((prev) => ({ ...prev, [tabIndex]: err }));
    } finally {
      setTabLoading((prev) => ({ ...prev, [tabIndex]: false }));
    }
  }, []);

  useEffect(() => {
    if (activeTab > 0 && TAB_TYPE_MAP[activeTab]) {
      fetchTabData(activeTab, currentPage);
    }
  }, [activeTab, currentPage, fetchTabData]);

  const handlePageChange = (tabIndex, newPage) => {
    setCurrentPages((prev) => ({ ...prev, [tabIndex]: newPage }));
  };

  const getTotalPages = (tabIndex) => Math.ceil((totalCounts[tabIndex] || 0) / DEFAULT_PAGE_SIZE);

  const tabConfig = [
    { title: KYC_TAB_TITLES.RFI_SUMMARY, data: formatSummaryData(summaryData) },
    { title: KYC_TAB_TITLES.REQUESTED_RFI, data: tabData[1] || [] },
    { title: KYC_TAB_TITLES.SUBMITTED_RFI, data: tabData[2] || [] },
    { title: KYC_TAB_TITLES.IN_REVIEW_RFI, data: tabData[3] || [] },
    { title: KYC_TAB_TITLES.DELAYED_RFI, data: tabData[4] || [] },
  ];

  const handleSummaryRowClick = (status) => {
    const statusToTabIndex = {
      [RFI_STATUS.RFI_REQUESTED]: 1,
      [RFI_STATUS.RFI_SUBMITTED]: 2,
      [RFI_STATUS.IN_REVIEW]: 3,
    };

    if (statusToTabIndex[status] !== undefined) {
      queryParams.set('tab', statusToTabIndex[status]);
      history.push({ search: queryParams.toString() });
    }
  };

  const handleSearch = (entityId) => {
    const route = `${ROUTES.KYC_RFI_ENTITY_DETAILS_ROUTE}${entityId}`;

    history.push(route, { entityId });
  };

  return (
    <Box mt={6} mb={3}>
      <Flex justify='flex-end' mb={3}>
        <Button colorScheme='teal' size='sm' onClick={globalLinkModal.onOpen}>
          Create Link
        </Button>
      </Flex>
      <Tabs
        isFitted
        variant='enclosed-colored'
        isLazy
        index={activeTab}
        onChange={(tab) => {
          queryParams.set('tab', tab);
          history.push({ search: queryParams.toString() });
        }}
      >
        <TabList>
          {tabConfig.map(({ title }, index) => (
            <Tab key={index}>{title}</Tab>
          ))}
          <Tab key='initiated-on-partner'>{KYC_TAB_TITLES.INITIATED_ON_PARTNER}</Tab>
        </TabList>
        <VStack spacing={4} align='stretch' mt={4} p={5}>
          <Grid templateColumns='repeat(2, 1fr)' gap={3}>
            <GridItem>
              <FormControl>
                <FormLabel>Entity ID</FormLabel>
                <Input
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  placeholder='Enter Entity ID'
                  onKeyDown={(e) => e.key === 'Enter' && entityId && handleSearch(entityId)}
                />
              </FormControl>
            </GridItem>
            <GridItem alignSelf='end'>
              <Button colorScheme='blue' onClick={() => handleSearch(entityId)} w='fit-content'>
                Proceed
              </Button>
            </GridItem>
          </Grid>
        </VStack>
        <TabPanels>
          {tabConfig.map(({ title, data }, index) => (
            <TabPanel key={index}>
              <LoadingErrorWrapper
                isLoading={index === 0 ? isLoading : tabLoading[index]}
                errorTitle={index === 0 ? 'Error fetching RFI summary.' : `Error fetching ${title}.`}
                error={index === 0 ? error : tabError[index]}
              >
                <RFiList
                  title={title}
                  rfiList={data}
                  isRfiSummary={title === KYC_TAB_TITLES.RFI_SUMMARY}
                  onSummaryRowClick={handleSummaryRowClick}
                  tab={activeTab}
                  currentPage={index > 0 ? currentPages[index] : undefined}
                  totalPages={index > 0 ? getTotalPages(index) : undefined}
                  totalCount={index > 0 ? totalCounts[index] : undefined}
                  isLastPage={index > 0 ? isLastPage[index] : undefined}
                  onPageChange={index > 0 ? (page) => handlePageChange(index, page) : undefined}
                />
              </LoadingErrorWrapper>
            </TabPanel>
          ))}
          <TabPanel key='initiated-on-partner'>
            <PartnerInitiatedKycs />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <CreateKybLinkModal isOpen={globalLinkModal.isOpen} onClose={globalLinkModal.onClose} requireEmail={false} />
    </Box>
  );
};

export default RfiSummary;
